import { useEffect, useState } from 'react'
import { useNavigate } from 'react-router-dom'
import client from '../api/client'
import { useAuthStore } from '../store/authStore'
import { useCounselingStore } from '../store/counselingStore'
import Spinner from '../components/Spinner'
import { toast } from '../components/Toast'

const CATEGORIES = [
  { key: 'all', label: '전체' },
  { key: 'love', label: '💕 연애' },
  { key: 'career', label: '💼 직장' },
  { key: 'relationship', label: '👥 인간관계' },
  { key: 'money', label: '💰 재물' },
  { key: 'health', label: '🌿 건강' },
  { key: 'general', label: '🔮 기타' },
]

const CATEGORY_LABELS: Record<string, string> = {
  love: '💕 연애/결혼', career: '💼 직업/커리어', relationship: '👥 인간관계',
  money: '💰 재물/재테크', health: '🌿 건강', general: '🔮 전체 운세',
}

function timeAgo(dateStr: string) {
  const diff = Date.now() - new Date(dateStr).getTime()
  const mins = Math.floor(diff / 60000)
  if (mins < 60) return `${mins}분 전`
  const hrs = Math.floor(mins / 60)
  if (hrs < 24) return `${hrs}시간 전`
  const days = Math.floor(hrs / 24)
  if (days < 7) return `${days}일 전`
  return new Date(dateStr).toLocaleDateString('ko-KR', { month: 'long', day: 'numeric' })
}

export default function History() {
  const navigate = useNavigate()
  const { isAuthenticated } = useAuthStore()
  const { sessions, setSessions, setCurrentSession, setMessages } = useCounselingStore()
  const [loading, setLoading] = useState(false)
  const [activeCategory, setActiveCategory] = useState('all')

  useEffect(() => {
    if (!isAuthenticated) { navigate('/login'); return }
    setLoading(true)
    client.get('/counseling/sessions')
      .then((r) => setSessions(r.data))
      .catch(() => toast('세션 목록을 불러오지 못했습니다.', 'error'))
      .finally(() => setLoading(false))
  }, [isAuthenticated])

  const deleteSession = async (id: number, e: React.MouseEvent) => {
    e.stopPropagation()
    if (!confirm('이 상담 세션을 삭제할까요?')) return
    try {
      await client.delete(`/counseling/sessions/${id}`)
      setSessions(sessions.filter((s) => s.id !== id))
      toast('삭제되었습니다.', 'success')
    } catch {
      toast('삭제에 실패했습니다.', 'error')
    }
  }

  const resumeSession = async (s: any, e: React.MouseEvent) => {
    e.stopPropagation()
    setCurrentSession(s)
    try {
      const { data } = await client.get(`/counseling/sessions/${s.id}/messages`)
      setMessages(data)
    } catch {}
    navigate('/counseling')
  }

  const filtered = activeCategory === 'all' ? sessions : sessions.filter((s) => s.category === activeCategory)

  return (
    <div className="min-h-screen bg-[var(--color-bg)] p-4 sm:p-6">
      <div className="max-w-2xl mx-auto">
        <div className="flex items-center gap-3 mb-6">
          <button onClick={() => navigate('/dashboard')} className="text-[var(--color-text-tertiary)] hover:text-[var(--color-text-primary)] transition-colors p-1">
            <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" />
            </svg>
          </button>
          <h1 className="text-xl font-bold text-[var(--color-text-primary)]">상담 내역</h1>
        </div>

        {/* 카테고리 필터 */}
        <div className="flex gap-2 overflow-x-auto pb-2 mb-4 scrollbar-hide">
          {CATEGORIES.map((cat) => (
            <button key={cat.key} onClick={() => setActiveCategory(cat.key)}
              className={`flex-shrink-0 px-3 py-1.5 rounded-full text-xs font-medium transition-colors ${
                activeCategory === cat.key
                  ? 'bg-[var(--color-accent)] text-[var(--color-primary)]'
                  : 'bg-[var(--color-surface)] text-[var(--color-text-tertiary)] hover:text-[var(--color-text-primary)]'
              }`}>
              {cat.label}
            </button>
          ))}
        </div>

        {loading ? (
          <div className="flex justify-center py-20"><Spinner size="lg" /></div>
        ) : filtered.length === 0 ? (
          <div className="text-center text-[var(--color-text-tertiary)] py-20">
            <p className="text-4xl mb-4">🏪</p>
            <p className="mb-1">
              {activeCategory === 'all' ? '아직 상담 내역이 없어요.' : '해당 카테고리의 상담이 없어요.'}
            </p>
            <p className="text-[var(--color-text-disabled)] text-sm mb-6">첫 상담을 시작해볼까요? 💬</p>
            <button onClick={() => navigate('/counseling')}
              className="px-5 py-2.5 bg-[var(--color-accent)] hover:opacity-90 text-[var(--color-primary)] rounded-xl text-sm font-bold transition-opacity">
              상담 시작하기
            </button>
          </div>
        ) : (
          <div className="space-y-3">
            {filtered.map((s) => (
              <div key={s.id}
                className="bg-[var(--color-surface)] border border-[var(--color-border-default)] rounded-xl p-4 hover:border-[var(--color-border-accent)] transition-colors">
                <div className="flex items-start justify-between gap-3">
                  <div className="min-w-0 flex-1">
                    <div className="text-[var(--color-text-primary)] font-medium truncate">{s.title || '새 상담'}</div>
                    <div className="text-[var(--color-text-tertiary)] text-xs mt-1">
                      {CATEGORY_LABELS[s.category]} · {s.mode === 'chat' ? '대화형' : 'Q&A형'}
                    </div>
                    <div className="text-[var(--color-text-disabled)] text-xs mt-0.5">{timeAgo(s.updated_at)}</div>
                  </div>
                  <div className="flex gap-2 flex-shrink-0">
                    <button onClick={(e) => resumeSession(s, e)}
                      className="px-3 py-1.5 bg-[var(--color-accent)]/10 hover:bg-[var(--color-accent)]/20 border border-[var(--color-accent)]/30 text-[var(--color-accent)] rounded-lg text-xs font-medium transition-colors">
                      이어하기
                    </button>
                    <button onClick={(e) => deleteSession(s.id, e)}
                      className="text-[var(--color-text-disabled)] hover:text-[var(--color-error)] transition-colors p-1.5 text-lg">
                      🗑
                    </button>
                  </div>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  )
}
